import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Organizations } from "@app/shared/src/api/api.gen";
import { Organization } from "@app/shared/src/models";
import { useSession } from "./SessionProvider";

/**
 * Which organization the console is looking at.
 *
 * Most directors belong to exactly one org, so this usually resolves silently. When a user spans
 * several (Omnisight staff, resellers), the topbar shows a switcher. The choice is remembered
 * locally rather than written to the server: last-viewed-org on the profile belongs to the main
 * dashboard's flow, and two apps silently fighting over it would surprise both.
 *
 * A user with no organization at all is the onboarding case: the shell sends them to the welcome
 * page, whose form calls `createOrg` — the server makes the creator an administrator, so the new
 * organization is in the list on the reload that follows, and the console switches to it.
 */

const STORAGE_KEY = "tb.orgId";

type OrgState = {
    orgs: Organization[];
    org?: Organization;
    setOrgId: (id: string) => void;
    /** True until the list has been fetched for the signed-in user; "no organizations" means nothing before then. */
    isLoading: boolean;
    error?: string;
    /** Re-fetch the list — after a change made elsewhere (an invite accepted on the dashboard). */
    reload: () => Promise<void>;
    /** Create an organization, refresh the list and switch to it. Resolves to the new id. */
    createOrg: (org: Organization) => Promise<string>;
};

const OrgContext = createContext<OrgState | undefined>(undefined);

export function useOrg(): OrgState {
    const value = useContext(OrgContext);
    if (!value) throw new Error("useOrg must be used inside OrgProvider");
    return value;
}

export const OrgProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useSession();
    const [orgs, setOrgs] = useState<Organization[]>([]);
    const [orgId, setOrgIdState] = useState<string | undefined>(() => localStorage.getItem(STORAGE_KEY) ?? undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>();
    // Only the latest fetch may land: a reload racing the sign-in fetch must not overwrite it.
    const seq = useRef(0);

    const load = useCallback(async () => {
        const mySeq = ++seq.current;
        setIsLoading(true);
        try {
            const list = (await Organizations.getAll()) ?? [];
            if (mySeq !== seq.current) return;
            // Alphabetical: the switcher is a lookup, not a ranking, and the server's order is
            // whatever the query happened to return.
            setOrgs([...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")));
            setError(undefined);
        } catch (err) {
            if (mySeq === seq.current) setError(err instanceof Error ? err.message : "Could not load organizations");
        } finally {
            if (mySeq === seq.current) setIsLoading(false);
        }
    }, []);

    // Keyed on the id, not the identity object: a profile edit re-fetches the identity and must not
    // re-fetch the organizations with it. Signing out clears the list and puts the loading flag
    // back, so the next sign-in cannot be mistaken for "no organizations" before its fetch lands.
    const userId = user?.id;
    useEffect(() => {
        if (!userId) {
            seq.current++;
            setOrgs([]);
            setIsLoading(true);
            setError(undefined);
            return;
        }
        void load();
    }, [userId, load]);

    const setOrgId = useCallback((id: string) => {
        localStorage.setItem(STORAGE_KEY, id);
        setOrgIdState(id);
    }, []);

    const createOrg = useCallback(
        async (org: Organization) => {
            const id = await Organizations.save(org);
            if (!id) throw new Error("The organization was not created");
            await load();
            setOrgId(id);
            return id;
        },
        [load, setOrgId],
    );

    const org = useMemo(() => {
        if (orgs.length === 0) return undefined;
        // Stored choice → profile's last-viewed → the only/first org. Falling through rather than
        // erroring: a director with one org should never see an org chooser.
        return (
            orgs.find((o) => o.id === orgId) ??
            orgs.find((o) => o.id === user?.lastViewedOrganizationId) ??
            orgs[0]
        );
    }, [orgs, orgId, user?.lastViewedOrganizationId]);

    const value = useMemo(
        () => ({ orgs, org, setOrgId, isLoading, error, reload: load, createOrg }),
        [orgs, org, setOrgId, isLoading, error, load, createOrg],
    );

    return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
};
